import { useMemo } from "react";
import createDOMPurify from "dompurify";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";

import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import { OptionType } from "@/shared/utils/constants";
import { useGetAnswersQuery, useGetQuestionByIdQuery } from "@/redux/service";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import { AnswerResponse } from "@/redux/survey/types";
import { isPaginatedResponse } from "@/redux/typeGuards";
import EvidencePreviewAccordion from "./EvidencePreviewAccordion";
import SurveyAnswerContent from "./SurveyAnswerContent";
import { hasSurveyAnswerContent } from "../utils/surveyAnswerDisplay";
import type {
  OverviewQuestionCardAnswersProps,
  OverviewQuestionCardProps,
} from "../types";

import "../styles.css";

const DOMPurify = createDOMPurify(window);

const OverviewQuestionCardAnswers = ({
  question,
  answers,
  evidenceBlocks,
  isLoading,
  hasDisplayableContent,
}: OverviewQuestionCardAnswersProps) => {
  const { t } = useTranslation();

  if (isLoading) {
    return <BaseSpinner size="small" />;
  }

  if (!hasDisplayableContent) {
    return (
      <p className="body-m-regular">{t("survey.labels.noAnswerProvided")}</p>
    );
  }

  return (
    <div className="flex flex-col gap-300">
      <SurveyAnswerContent question={question} answers={answers} />
      <EvidencePreviewAccordion blocks={evidenceBlocks} />
    </div>
  );
};

const OverviewQuestionCard = ({
  questionId,
  questionIndex,
}: OverviewQuestionCardProps) => {
  const [searchParams] = useSearchParams();
  const currentUser = useSelector(selectCurrentUser);

  const organization =
    searchParams.get("organization") || currentUser?.organization?.id || null;

  const {
    data: answersData,
    isLoading: isAnswersQueryLoading,
    isFetching: isAnswersFetching,
    isUninitialized: isAnswersUninitialized,
  } = useGetAnswersQuery(
    {
      question: questionId,
      organization: organization as number | null,
      pagination: "off",
    },
    { skip: organization == null },
  );

  const { data: question, isLoading: isQuestionLoading } =
    useGetQuestionByIdQuery({ questionId });

  const answers: AnswerResponse[] =
    answersData == null
      ? []
      : isPaginatedResponse(answersData)
        ? answersData.results
        : answersData;

  const isAnswersLoading =
    isAnswersQueryLoading || isAnswersFetching || isAnswersUninitialized;

  const evidenceBlocks = useMemo(() => {
    if (!question) {
      return [];
    }

    const promptOptionIds = new Set(
      question.options
        .filter((option) => option.type === OptionType.PROMPT.value)
        .map((option) => option.id),
    );

    return answers
      .filter((answer) => promptOptionIds.has(answer.option))
      .flatMap((answer) => answer.attachedBlocks);
  }, [question, answers]);

  if (!currentUser) {
    return null;
  }

  if (isQuestionLoading || !question) {
    return (
      <div className="overview-question-card flex justify-center full-width">
        <BaseSpinner size="small" />
      </div>
    );
  }

  const hasNonEvidenceAnswers = hasSurveyAnswerContent(question, answers);
  const hasEvidence = evidenceBlocks.length > 0;
  const hasDisplayableContent = hasNonEvidenceAnswers || hasEvidence;

  return (
    <div className="overview-question-card flex flex-col gap-300">
      <div className="flex flex-col gap-200">
        <h3 className="header-s-medium">
          {questionIndex + 1}. {question.label}
        </h3>

        {question.description ? (
          <div
            className="question-description body-m-regular"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(question.description, {
                USE_PROFILES: { html: true },
                ADD_ATTR: ["target"],
              }),
            }}
          />
        ) : null}
      </div>

      <OverviewQuestionCardAnswers
        question={question}
        answers={answers}
        evidenceBlocks={evidenceBlocks}
        isLoading={isAnswersLoading}
        hasDisplayableContent={hasDisplayableContent}
      />
    </div>
  );
};

export default OverviewQuestionCard;
